/**
 * Wiki content renderers.
 *
 * <HubBody>   — renders a country-hub cover essay (`HubArticle.cover`).
 * <TopicBody> — renders a topic article (lead + sections + faqs).
 *
 * Both pull a `FactsRegistry` for the current article + language and
 * interpolate `{{fact.<id>}}` tokens at render time.  Authored prose may
 * contain a small inline-HTML vocabulary (<strong>, <em>, <a>, <code>) —
 * everything else is stripped to keep the rendered surface safe.
 */

import { useState, useEffect, useCallback, type ReactNode } from 'react';
import type {
  ArticleSection,
  ContentBlock,
  FaqEntry,
  HeroImage as HeroImageData,
  HubArticle,
  TopicArticle,
} from './content-types';
import type { CountryCode, LangCode } from './architecture';
import type { WidgetPlan } from './page-plan';
import { useFactsForArticle, type FactsRegistry } from './facts';
import { renderWidget } from './widgets';
import { Term } from './Term';
import { getGlossaryEntry } from './glossary';

/* ────────────────────────────────────────────────────────────────── */
/*  Sanitize-and-render helper                                         */
/* ────────────────────────────────────────────────────────────────── */
/*  Authored content is trusted (it ships in the bundle), but we keep   */
/*  a narrow allowlist to prevent accidental tag injection from breaking */
/*  the page.  Anything outside the allowlist is stripped to text.      */

const ALLOWED_TAGS_RE =
  /<(\/?)(strong|em|a|code)((?:\s+[a-z-]+="[^"<>]*")*)\s*\/?>/gi;

function sanitizeInlineHtml(input: string): string {
  // Replace stray < / > that aren't part of an allowlisted tag with entities.
  const tags: string[] = [];
  const masked = input.replace(ALLOWED_TAGS_RE, (m) => {
    tags.push(m);
    return ` TAG${tags.length - 1} `;
  });
  const escaped = masked
    .replace(/&(?!(?:amp|lt|gt|quot|#\d+);)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(/ TAG(\d+) /g, (_, i) => tags[Number(i)]);
}

/**
 * Split an HTML string by `[[term-id]]` / `[[term-id|surface]]` markers and
 * render the result as a React node sequence: HTML segments via
 * dangerouslySetInnerHTML, glossary markers via <Term>.  The split protects
 * authored markers from the HTML-sanitiser pass (which would otherwise
 * escape the brackets) and keeps tooltips interactive.
 */
function renderProseWithTerms(html: string, lang: LangCode): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\[\[([a-z0-9-]+)(?:\|([^\]]+))?\]\]/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(html))) {
    if (match.index > cursor) {
      out.push(
        <span
          key={`h${key++}`}
          dangerouslySetInnerHTML={{ __html: html.slice(cursor, match.index) }}
        />,
      );
    }
    out.push(<Term key={`t${key++}`} id={match[1]} surface={match[2]} lang={lang} />);
    cursor = match.index + match[0].length;
  }
  if (cursor < html.length) {
    out.push(
      <span key={`h${key}`} dangerouslySetInnerHTML={{ __html: html.slice(cursor) }} />,
    );
  }
  return out;
}

function Prose({
  text,
  registry,
  lang,
  className,
  as: Tag = 'p',
}: {
  text: string;
  registry: FactsRegistry;
  lang: LangCode;
  className?: string;
  as?: 'p' | 'span' | 'li' | 'h3' | 'blockquote';
}) {
  const html = sanitizeInlineHtml(registry.interpolate(text));
  const nodes = renderProseWithTerms(html, lang);
  return <Tag className={className}>{nodes}</Tag>;
}

/* ────────────────────────────────────────────────────────────────── */
/*  Block renderer                                                     */
/* ────────────────────────────────────────────────────────────────── */

function renderBlock(
  block: ContentBlock,
  registry: FactsRegistry,
  key: number,
  widgets: Map<string, WidgetPlan>,
  lang: LangCode,
): ReactNode {
  switch (block.kind) {
    case 'p':
      return (
        <Prose
          key={key}
          text={block.text}
          registry={registry}
          lang={lang}
          className="mb-4 text-[15px] leading-relaxed text-ink-800 sm:text-base sm:leading-[1.7]"
        />
      );
    case 'h3':
      return (
        <Prose
          key={key}
          text={block.text}
          registry={registry}
          lang={lang}
          as="h3"
          className="mt-8 mb-3 font-display text-lg font-medium italic tracking-tight text-ink-900 sm:text-xl"
        />
      );
    case 'list': {
      const Container = block.ordered ? 'ol' : 'ul';
      return (
        <Container
          key={key}
          className={`mb-5 ml-5 space-y-1.5 text-[15px] leading-relaxed text-ink-800 sm:text-base ${
            block.ordered ? 'list-decimal' : 'list-disc'
          }`}
        >
          {block.items.map((it, i) => (
            <Prose key={i} text={it} registry={registry} lang={lang} as="li" />
          ))}
        </Container>
      );
    }
    case 'quote':
      return (
        <figure key={key} className="my-6 border-l-2 border-terra-600/60 pl-4">
          <Prose
            text={block.text}
            registry={registry}
            lang={lang}
            as="blockquote"
            className="font-display text-base italic text-ink-700 sm:text-lg"
          />
          {block.cite && (
            <figcaption className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-500">
              — {block.cite}
            </figcaption>
          )}
        </figure>
      );
    case 'note':
      return (
        <Prose
          key={key}
          text={block.text}
          registry={registry}
          lang={lang}
          className="mt-2 mb-4 text-xs text-ink-500"
        />
      );
    case 'widget': {
      // Real widget mounting — look up by id in plan.interactive[], dispatch
      // to the registered component.  Falls back to a labelled stub when
      // the id is unknown so an author typo is visible in dev.
      const plan = widgets.get(block.widgetId);
      if (!plan) {
        return (
          <div
            key={key}
            aria-hidden="true"
            className="my-6 rounded-lg border border-dashed border-rust-500/40 bg-paper-2/60 px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-rust-600"
          >
            missing widget plan · {block.widgetId}
          </div>
        );
      }
      return (
        <div key={key} id={`widget-${plan.id}`}>
          {renderWidget({ plan, registry, lang })}
        </div>
      );
    }
    default:
      return null;
  }
}

/* ────────────────────────────────────────────────────────────────── */
/*  Image lightbox                                                      */
/* ────────────────────────────────────────────────────────────────── */
/*  Fullscreen overlay that shows a large version of the hero image.    */
/*  Closes on backdrop click, × button, or Escape key.                  */

function ImageLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Prevent body scroll while lightbox is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/80 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      {/* Backdrop close button — fills the area behind the image */}
      <button
        type="button"
        aria-label="Close image"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default focus:outline-none"
        tabIndex={-1}
      />

      {/* Close × button */}
      <button
        type="button"
        aria-label="Close image"
        onClick={onClose}
        className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/50"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <line x1="2" y1="2" x2="16" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <line x1="16" y1="2" x2="2" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {/* Image — sits above the backdrop button via relative positioning */}
      <img
        src={src}
        alt={alt}
        className="relative z-10 max-h-full max-w-full rounded-lg object-contain shadow-2xl cursor-zoom-out"
        draggable={false}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Hero image                                                         */
/* ────────────────────────────────────────────────────────────────── */
/*  Renders a wide cover illustration with optional caption + credit.   */
/*  Clicking opens a fullscreen lightbox so fine text is readable.      */

export function HeroImageBlock({ hero }: { hero: HeroImageData }) {
  const [open, setOpen] = useState(false);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <figure className="mb-8 sm:mb-10">
        <div className="overflow-hidden rounded-lg border border-ink-100 bg-paper-2">
          <button
            type="button"
            aria-label="Enlarge image"
            onClick={() => setOpen(true)}
            className="block w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-terra-600"
          >
            <img
              src={hero.src}
              alt={hero.alt}
              loading="eager"
              decoding="async"
              className="aspect-[1200/630] w-full object-cover cursor-zoom-in"
              width={1200}
              height={630}
            />
          </button>
        </div>
        {(hero.caption || hero.credit) && (
          <figcaption className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-wider text-ink-500">
            {hero.caption && <span className="italic normal-case tracking-normal text-ink-700">{hero.caption}</span>}
            {hero.credit && <span>{hero.credit}</span>}
          </figcaption>
        )}
      </figure>

      {open && <ImageLightbox src={hero.src} alt={hero.alt} onClose={handleClose} />}
    </>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Hub body                                                           */
/* ────────────────────────────────────────────────────────────────── */

export function HubBody({
  article,
  cc,
  lang,
}: {
  article: HubArticle;
  cc: CountryCode;
  lang: LangCode;
}) {
  const registry = useFactsForArticle(article.plan.facts, cc, lang);
  const widgets = new Map(article.plan.interactive.map((w) => [w.id, w]));
  return (
    <section aria-label="Cover essay" className="mb-12">
      {article.hero && <HeroImageBlock hero={article.hero} />}
      {article.cover.map((b, i) => renderBlock(b, registry, i, widgets, lang))}
      {registry.freshness && (
        <p className="mt-6 font-mono text-[10px] uppercase tracking-wider text-ink-400">
          {lang === 'ru'
            ? `Проверено · ${registry.freshness}`
            : `Verified · ${registry.freshness}`}
        </p>
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Topic body                                                         */
/* ────────────────────────────────────────────────────────────────── */

export function TopicBody({
  article,
  cc,
  lang,
}: {
  article: TopicArticle;
  cc: CountryCode;
  lang: LangCode;
}) {
  const registry = useFactsForArticle(article.plan.facts, cc, lang);
  const widgets = new Map(article.plan.interactive.map((w) => [w.id, w]));

  return (
    <>
      {article.hero && <HeroImageBlock hero={article.hero} />}
      <Lead text={article.lead} registry={registry} lang={lang} />

      {article.sections.map((s) => (
        <Section key={s.id} section={s} registry={registry} widgets={widgets} lang={lang} />
      ))}

      <FaqList faqs={article.faqs} registry={registry} lang={lang} />

      {registry.freshness && (
        <p className="mt-10 font-mono text-[10px] uppercase tracking-wider text-ink-400">
          {lang === 'ru'
            ? `Проверено · ${registry.freshness}`
            : `Verified · ${registry.freshness}`}
        </p>
      )}
    </>
  );
}

function Lead({ text, registry, lang }: { text: string; registry: FactsRegistry; lang: LangCode }) {
  return (
    <Prose
      text={text}
      registry={registry}
      lang={lang}
      className="mb-8 text-lg leading-relaxed text-ink-800 sm:text-xl sm:leading-[1.55]"
    />
  );
}

function Section({
  section,
  registry,
  widgets,
  lang,
}: {
  section: ArticleSection;
  registry: FactsRegistry;
  widgets: Map<string, WidgetPlan>;
  lang: LangCode;
}) {
  return (
    <section id={section.id} className="mt-10">
      <h2 className="mb-4 font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl">
        {section.heading}
      </h2>
      {section.blocks.map((b, i) => renderBlock(b, registry, i, widgets, lang))}
    </section>
  );
}

function FaqList({
  faqs,
  registry,
  lang,
}: {
  faqs: FaqEntry[];
  registry: FactsRegistry;
  lang: LangCode;
}) {
  if (faqs.length === 0) return null;
  return (
    <section aria-labelledby="faq-title" className="mt-12">
      <h2
        id="faq-title"
        className="mb-4 font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
      >
        {lang === 'ru' ? 'Часто спрашивают' : 'Frequently asked'}
      </h2>
      <dl className="space-y-3">
        {faqs.map((f, i) => (
          <details
            key={i}
            className="group rounded-lg border border-ink-100 bg-paper-2/60 open:bg-paper-2"
          >
            <summary className="flex cursor-pointer items-baseline justify-between gap-3 px-4 py-3 text-sm font-medium text-ink-900 sm:text-base">
              <span>{f.question}</span>
              <span aria-hidden="true" className="text-ink-400 group-open:rotate-180 transition-transform motion-reduce:transition-none">▾</span>
            </summary>
            <Prose
              text={f.answer}
              registry={registry}
              lang={lang}
              className="px-4 pb-4 pt-1 text-[15px] leading-relaxed text-ink-700"
            />
          </details>
        ))}
      </dl>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  FAQ JSON-LD helper                                                 */
/* ────────────────────────────────────────────────────────────────── */
/*  Used by CountryTopicPage to feed the topic-article JSON-LD with    */
/*  resolved FAQ Q&A pairs (so Google rich snippets work).             */

// eslint-disable-next-line react-refresh/only-export-components
export function buildFaqsForJsonLd(
  faqs: FaqEntry[],
  registry: FactsRegistry,
  lang: LangCode,
): Array<{ question: string; answer: string }> {
  const stripTags = (s: string) => s.replace(/<[^>]+>/g, '');
  // Replace `[[term-id]]` / `[[term-id|surface]]` markers with their surface
  // text so search-engine snippets read as natural prose, not as authoring
  // syntax.  Surface defaults to the glossary label for the active language.
  const resolveTerms = (s: string): string =>
    s.replace(/\[\[([a-z0-9-]+)(?:\|([^\]]+))?\]\]/g, (_match, id: string, surface?: string) =>
      surface ?? getGlossaryEntry(id, lang)?.label ?? id,
    );
  return faqs.map((f) => ({
    question: f.question,
    answer: resolveTerms(stripTags(registry.interpolate(f.answer))),
  }));
}
